import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sun, Moon, Menu } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { useTheme } from '../../context/ThemeContext';
import { ROUTES } from '../../utils/constants';
import { adm } from '../admin/adminTheme';

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
  variant?: 'default' | 'admin';
  hideSidebar?: boolean;
}

export const Layout: React.FC<LayoutProps> = ({
  children,
  title,
  variant = 'default',
  hideSidebar = false,
}) => {
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAdmin = variant === 'admin';

  return (
    <div
      className={
        isAdmin
          ? `${adm.shell} flex overflow-x-hidden max-w-full`
          : 'min-h-screen bg-background flex overflow-x-hidden max-w-full'
      }
    >
      {!hideSidebar && <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />}

      <main
        className={
          hideSidebar
            ? `flex-1 min-h-screen w-full max-w-full overflow-x-hidden ${isAdmin ? adm.main : ''}`
            : isAdmin
              ? `flex-1 lg:ml-64 min-h-screen w-full lg:w-auto max-w-full overflow-x-hidden ${adm.main}`
              : 'flex-1 lg:ml-64 min-h-screen w-full lg:w-auto max-w-full overflow-x-hidden'
        }
      >
        <header
          className={
            isAdmin
              ? adm.header
              : 'sticky top-0 z-40 bg-background-secondary/80 backdrop-blur-sm border-b border-border'
          }
        >
          <div className={isAdmin ? adm.headerInner : 'px-4 md:px-6 lg:px-8 py-4 lg:py-6 flex items-center justify-between min-h-[64px] lg:min-h-[88px] gap-3'}>
            <div className="flex items-center gap-3 min-w-0">
              {!hideSidebar && (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className={
                    isAdmin
                      ? 'lg:hidden p-2 rounded-xl border border-white/[0.08] bg-white/[0.04] text-[#c8cdd8] hover:bg-white/[0.07]'
                      : 'lg:hidden p-2 rounded-lg hover:bg-background-tertiary transition-smooth'
                  }
                  aria-label="Menüyü aç"
                >
                  <Menu className={isAdmin ? 'w-5 h-5' : 'w-5 h-5 text-foreground'} />
                </button>
              )}
              <div className="min-w-0">
                <h1
                  className={
                    isAdmin
                      ? adm.title
                      : 'text-lg md:text-xl lg:text-2xl font-semibold text-foreground tracking-tight truncate'
                  }
                >
                  {isAdmin ? title || 'Ingenium Admin Panel' : title || 'StokFlow'}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              {isAdmin && (
                <Link to={ROUTES.DASHBOARD} className={adm.linkBack}>
                  <ArrowLeft className="h-3.5 w-3.5 opacity-80" />
                  Ana uygulama
                </Link>
              )}
              {!isAdmin && (
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="inline-flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg border border-border bg-background-tertiary text-foreground-secondary hover:text-foreground hover:bg-background transition-smooth text-xs md:text-sm"
                  aria-label="Tema değiştir"
                >
                  {theme === 'dark' ? (
                    <>
                      <Sun className="w-3.5 h-3.5 md:w-4 md:h-4" />
                      <span className="hidden sm:inline">Işık Modu</span>
                    </>
                  ) : (
                    <>
                      <Moon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                      <span className="hidden sm:inline">Karanlık Mod</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </header>

        <div className={isAdmin ? adm.contentWrap : 'px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-8'}>
          {children}
        </div>
      </main>
    </div>
  );
};
